import {
    Autocomplete,
    Box, createFilterOptions, Input,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow, TextField,
    Tooltip, Typography
} from "@mui/material";
import React, {useEffect, useState, forwardRef, useMemo} from "react";
import {useDebounce} from "../../../../hooks/useDebounce";
import {ConversationTableRow} from "./ConversationTableRow";
import DatePicker from "react-datepicker";
import moment from "moment";
import {useAtom} from "jotai";
import {SelectedConversationAtom} from "../atoms";
import {useDashboard} from "../../../../context/DashboardContext";
import {IconPhone,IconMessage2} from "@tabler/icons-react";
import {grey} from "@mui/material/colors";

const DateRangeInput = forwardRef(({value, onClick, placeholder}, ref) => (
    <Input
        className={"table-input"}
        disableUnderline={true}
        sx={{
            width: "100%",
            borderRadius: 2,
            border: "none !important",
            px: 1,
            py: 0.25,
            fontSize: 11,
            bgcolor: "grey.200",
            cursor: "pointer"
        }}
        value={value}
        placeholder={placeholder}
        onClick={onClick}
        inputRef={ref}
        readOnly
    />
));

export const ConversationsTable = ({project}) => {
    const {conversations, pagination, setPagination, refreshConversations, updatedValue} = useDashboard();
    const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);
    const [filters, setFilters] = useState({
        id: "",
        start: null,
        end: null,
        caller: "",
        status: null,
        vdn: "",
        tags: [],
    })
    const filtersDebounced = useDebounce(filters, 500);
    const phoneBook = useMemo(() => project?.phonebook || [], [project?.phonebook]);
    const phoneBookOptions = Object.entries(phoneBook).map(([phone, name]) => ({
        phone,
        name
    }));
    const phoneBookFilterOptions = createFilterOptions({
        stringify: (option) => `${option?.name || ""} ${option?.phone || ""}`
    });

    useEffect(() => {
        if (conversations && conversations?.length && selectedConversation?.id) {
            const conversation = conversations?.find(i => i?.id === selectedConversation?.id);
            if (conversation) {
                setSelectedConversation(conversation);
            }
        }
    }, [conversations, selectedConversation?.id])

    useEffect(() => {
        if (project?.id) {
            const getDate = date => {
                return date.getTime();
            }

            const formattedFilters = {
                ...filtersDebounced,
                start: filtersDebounced?.start ? getDate(filtersDebounced?.start) : undefined,
                end: filtersDebounced?.end ? getDate(filtersDebounced?.end) : undefined,
                tagIds: filtersDebounced?.tags?.length ? filtersDebounced.tags.map(t => t?.id) : undefined,
            };

            refreshConversations(formattedFilters);

            const interval = setInterval(() => {
                refreshConversations(formattedFilters);
            }, 60 * 1000);

            return () => clearInterval(interval);
        }
    }, [project?.id, pagination?.page, pagination?.size, filtersDebounced, updatedValue, refreshConversations])

    const scrollConversations = (elementId = "conversation-tbody") => {
        const tbody = document?.getElementById(elementId);
        if (tbody) {
            tbody?.scrollIntoView({behavior: "smooth", block: "end"})
        } else {
            throw new Error("Element not found")
        }
    }

    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        <Stack direction="column" gap={0} sx={{borderRadius: 3}}
               className={"border-shadow"}>
            {/*<Box sx={{px: 2, pt: 2}}>*/}
            {/*  <Typography variant={"body1"} color={grey[500]}>CONVERSATION LOGGER</Typography>*/}
            {/*</Box>*/}
            <TableContainer sx={{maxHeight: "70dvh", minHeight: "50dvh"}}>
                <Table stickyHeader aria-label="sticky table" sx={{height: "100%", p: 2, pt: 1, tableLayout: "fixed"}}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding={"checkbox"} sx={{py: 0.5, px: 1, width: 30, maxWidth: 30}}>
                                <Input
                                    className={"table-input"}
                                    disableUnderline
                                    sx={{
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.25,
                                        fontSize: 11,
                                        bgcolor: "grey.200",
                                        width: "100%"
                                    }}
                                    value={filters?.id} placeholder={"ID"}
                                    onChange={e => setFilters(prev => ({...prev, id: e?.target?.value}))}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 1, display: {xs: "none", md: "table-cell"}}}>
                                <Autocomplete
                                    freeSolo
                                    value={filters?.callerContact || filters?.caller || null}
                                    onChange={(event, newValue) => {
                                        if (typeof newValue === "string") {
                                            setFilters(prev => ({...prev, caller: newValue, callerContact: null}));
                                        } else {
                                            setFilters(prev => ({
                                                ...prev,
                                                caller: newValue?.phone || "",
                                                callerContact: newValue
                                            }));
                                        }
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === "input") {
                                            setFilters(prev => ({...prev, caller: newInputValue, callerContact: null}));
                                        }
                                    }}
                                    options={phoneBookOptions}
                                    getOptionLabel={(option) => {
                                        if (typeof option === "string") return option;
                                        return option?.name || "";
                                    }}
                                    filterOptions={phoneBookFilterOptions}
                                    isOptionEqualToValue={(option, value) => option?.phone === value?.phone}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 12, minHeight: "auto", padding: "4px 8px"}}>
                                            {typeof option === "string" ? option : option?.name || ""}
                                        </li>
                                    )}
                                    clearOnEscape
                                    className={"table-input"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Caller"
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11}
                                            }}
                                        />
                                    )}
                                    sx={{borderRadius: 2, px: 1, py: 0.25, fontSize: 11, bgcolor: "grey.200"}}
                                />
                            </TableCell>
                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 1, display: {xs: "none", md: "table-cell"}}}>
                                <DatePicker
                                    id={"date-range-picker"} name={"date-range-picker"}
                                    placeholderText={"Date Range"}
                                    dateFormat={"dd MMM"}
                                    selectsRange
                                    startDate={filters?.start}
                                    endDate={filters?.end}
                                    isClearable
                                    maxDate={new Date()}
                                    style={{border: "none !important", width: "100%"}}
                                    customInput={<DateRangeInput placeholder="Date Range"/>}
                                    onChange={(update) => {
                                        const [start, end] = update;

                                        const startOfDay = start
                                            ? moment(start).set({hour: 0, minute: 0, second: 0}).toDate()
                                            : null;
                                        const endOfDay = end
                                            ? moment(end).set({hour: 23, minute: 59, second: 59}).toDate()
                                            : null;

                                        setFilters((prev) => ({...prev, start: startOfDay, end: endOfDay}));
                                    }}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"} sx={{
                                py: 0.5, px: 1, width: type === "CHAT_BOT" ? 30 : 60,
                                maxWidth: type === "CHAT_BOT" ? 30 : 60
                            }}>
                                <Autocomplete
                                    value={filters.status || null}
                                    onChange={(event, newValue) => {
                                        setFilters(prev => ({...prev, status: newValue}));
                                    }}
                                    options={type === "VOICE_BOT"
                                        ? ["ACTIVE", "FINISHED", "REDIRECTED", "FAILED", "HANDOVER", "HANGUP", "MISSED_CALL"]
                                        : ["ACTIVE", "FINISHED", "FAILED"]}
                                    clearOnEscape
                                    freeSolo={false}
                                    className={"table-input"}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Status"
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 14, minHeight: "auto", padding: "4px 8px"}}>
                                            {option}
                                        </li>
                                    )}
                                    sx={{borderRadius: 2, px: 1, py: 0.25, fontSize: 11, bgcolor: "grey.200"}}
                                />
                            </TableCell>
                            <TableCell padding={"checkbox"}
                                       sx={{py: 0.5, px: 1, textWrap: "nowrap", width: "80px", maxWidth: "80px"}}>

                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    value={filters?.tags || []}
                                    onChange={(event, newValue) => {
                                        setFilters(prev => ({...prev, tags: newValue}));
                                    }}
                                    options={project?.settings?.tags || []}
                                    getOptionLabel={(option) => option?.title || option?.name || ""}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    clearOnEscape
                                    className={"table-input"}
                                    renderTags={(value) => (
                                        <Tooltip
                                            title={value?.length ? value.map(t => t?.title || t?.name).join(", ") : ""}
                                            arrow
                                            placement="bottom-start"
                                        >
                                            <Box component="span" sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: 18,
                                                height: 18,
                                                minWidth: 18,
                                                maxWidth: 18,
                                                minHeight: 18,
                                                maxHeight: 18,
                                                borderRadius: "50%",
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                fontSize: 12,
                                                fontWeight: 500,
                                                lineHeight: 1,
                                                cursor: "default",
                                                flexShrink: 0,
                                                boxSizing: "border-box"
                                            }}>
                                                {value?.length || ""}
                                            </Box>
                                        </Tooltip>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={filters?.tags?.length ? "" : "Tags"}
                                            className={"table-input"}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true,
                                                sx: {border: 0, fontSize: 11, flexWrap: "nowrap"}
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} style={{fontSize: 14, minHeight: "auto", padding: "4px 8px"}}>
                                            {option?.title || option?.name || ""}
                                        </li>
                                    )}
                                    sx={{
                                        borderRadius: 2,
                                        px: 1,
                                        py: 0.25,
                                        fontSize: 10,
                                        bgcolor: "grey.200",
                                        "& .MuiAutocomplete-inputRoot": {
                                            flexWrap: "nowrap",
                                            overflow: "hidden"
                                        }
                                    }}
                                />
                            </TableCell>

                            <TableCell padding={"checkbox"} sx={{
                                py: 0.5,
                                px: 0.5,
                                width: 30,
                                maxWidth: 30,
                                display: {xs: "none", md: "table-cell"}
                            }}></TableCell>


                            {type === "VOICE_BOT" ?
                                <TableCell padding={"checkbox"} sx={{py: 0.5, px: 1}}></TableCell> : null}
                        </TableRow>
                    </TableHead>
                    <TableBody id={"conversation-tbody"}>
                        {conversations?.length > 0 ? (
                            conversations?.map(item => (
                                <ConversationTableRow
                                    key={item?.id} conversation={item} project={project}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={type === "VOICE_BOT" ? 7 : 6} sx={{border: 0}}>
                                    <Box sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        py: 6,
                                        color: "grey.500",
                                        flexDirection: "column",
                                        gap: 1
                                    }}>
                                        {type === "VOICE_BOT" ?
                                            <IconPhone size={48} color={grey[300]}/> :
                                            <IconMessage2 size={48} color={grey[300]}/>
                                        }
                                        <Typography variant="body1" color={grey[500]}>No conversations found</Typography>
                                        <Typography variant="body2" color={grey[400]}>Try adjusting your filters</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Stack direction={"row"} sx={{width: "100%"}} justifyContent={"flex-end"}>
                <TablePagination
                    component="div"
                    count={pagination?.totalElements}
                    page={pagination?.page}
                    onPageChange={(e, page) => setPagination(prev => ({...prev, page: page}))}
                    rowsPerPage={pagination?.size}
                    onRowsPerPageChange={(e) => setPagination(prev => ({...prev, page: 0, size: +e.target.value}))}
                />
            </Stack>
        </Stack>
    )
}